import * as Sentry from '@sentry/react';

/**
 * Telemetry for the EMDR-Plus device connection indicator.
 *
 * The practitioner dashboard derives "headset connected" purely from the age of the
 * `timestamp` field on `sessions/currentState`. That field is the only part of the
 * response the backend serves from process memory, so a request handled by an instance
 * that never saw the device's status report comes back without it. Until we can tell
 * those two cases apart in production data, every drop is reported with enough context
 * to decide which one it was.
 */

type DeviceConnectionSnapshot = {
  /** Age of the heartbeat we based the decision on, in ms. Null when we never had one. */
  ageMs: number | null;
  /** The poll that triggered this drop came back without a `timestamp` field. */
  timestampMissing: boolean;
  /** The response had no `timestamp`, so the decision used the cached heartbeat. */
  usedStickyFallback: boolean;

  timestampRolledBack: boolean;
  /** Polls that failed or returned a non-object in a row, at the moment of the drop. */
  consecutiveFetchFailures: number;
  serialNumber: string | null;
};

type TrackedState = {
  isConnected: boolean;
  since: number;
  drops: number;
  lastReportAt: number;
};

/** Guards against report storms while the indicator is flapping every few seconds. */
const MIN_REPORT_INTERVAL_MS = 10_000;

/**
 * `useCurrentSessionState` is instantiated from ~7 places on the practitioner page, each
 * with its own polling interval. Tracking at module level keeps one drop from being
 * reported up to seven times.
 */
const trackedBySession = new Map<string, TrackedState>();

const getCurrentPageTag = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const path = window.location.pathname.toLowerCase();
  if (path.includes('clientpage')) return 'clientpage';
  if (path.includes('practitionerpage')) return 'practitionerpage';
  return path || 'unknown';
};

const getOnlineState = (): boolean | null => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') {
    return null;
  }

  return navigator.onLine;
};

const getVisibilityState = (): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  return document.visibilityState ?? null;
};

/**
 * Classifies why the indicator went dark, so the Sentry issue can be split without
 * reading every event. `no_state_from_backend` is the signature of the in-memory cache
 * miss; `heartbeat_expired` means the headset itself really did go quiet.
 */
const getDropReason = (snapshot: DeviceConnectionSnapshot): string => {
  if (snapshot.consecutiveFetchFailures > 0) return 'dashboard_fetch_failing';
  if (snapshot.timestampMissing) return 'no_state_from_backend';
  if (snapshot.ageMs === null) return 'never_seen';
  return 'heartbeat_expired';
};

/**
 * Records the current connection state for a session and reports drops to Sentry.
 * Safe to call on every render — only true -> false transitions produce an event.
 */
export const trackDeviceConnectionState = (
  sessionKey: string,
  isConnected: boolean,
  snapshot: DeviceConnectionSnapshot
): void => {
  const now = Date.now();
  const tracked = trackedBySession.get(sessionKey);

  if (!tracked) {
    // First observation for this session: record it, never report it. A dashboard that
    // opens before the headset is up would otherwise report a drop that never happened.
    trackedBySession.set(sessionKey, { isConnected, since: now, drops: 0, lastReportAt: 0 });
    return;
  }

  if (tracked.isConnected === isConnected) {
    return;
  }

  const wasConnected = tracked.isConnected;
  const connectedForMs = wasConnected ? now - tracked.since : null;

  tracked.isConnected = isConnected;
  tracked.since = now;

  if (!wasConnected) {
    return;
  }

  tracked.drops += 1;

  if (now - tracked.lastReportAt < MIN_REPORT_INTERVAL_MS) {
    return;
  }
  tracked.lastReportAt = now;

  const page = getCurrentPageTag();
  const reason = getDropReason(snapshot);

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setFingerprint(['device-connection-drop', page, reason]);
    scope.setTag('diagnostic_source', 'device');
    scope.setTag('device_diagnostic', 'connection_drop');
    scope.setTag('device_drop_reason', reason);
    scope.setTag('app_page', page);

    scope.setContext('device_connection_drop', {
      reason,
      ageMs: snapshot.ageMs,
      timestampMissing: snapshot.timestampMissing,
      usedStickyFallback: snapshot.usedStickyFallback,
      timestampRolledBack: snapshot.timestampRolledBack,
      consecutiveFetchFailures: snapshot.consecutiveFetchFailures,
      connectedForMs,
      dropsThisSession: tracked.drops,
      serialNumber: snapshot.serialNumber,
      visibilityState: getVisibilityState(),
      online: getOnlineState(),
    });

    Sentry.captureMessage(`Device connection lost (${reason})`, 'warning');
  });
};

/** Exposed for tests — the tracker is module state and would otherwise leak between cases. */
export const resetDeviceConnectionTracking = (): void => {
  trackedBySession.clear();
};
